class StrawSets
{
  // Straw-set purchase rules: for listed treatment countries, donor straws can
  // only be bought in whole multiples of a set size (e.g. Australia = sets of
  // 6). This is a THEME-LEVEL rule only — there is deliberately no backend or
  // checkout-app enforcement, so every storefront purchase path must run
  // through these helpers (PDP steppers, cart steppers, the cart-page gate in
  // custom-cart, and the native checkout-button interception below).
  //
  // Rules come from the "Straw set sizes per treatment country" theme setting
  // (settings.custom_logic_straw_set_rules, format "au:6,gb:12"), seeded into
  // window.theme.strawSetRules by theme-common-header-scripts.liquid. An empty
  // setting turns the whole feature off — that is the kill switch.

  // Lazily parsed rule map: { 'AU': 6 }. Parsed once per page load.
  getRules ()
  {
    if (this._rules) {
      return this._rules;
    }

    this._rules = {};

    const raw = (window.theme && window.theme.strawSetRules) || '';

    ('' + raw).split(',').forEach(rule => {
      const parts = rule.split(':');

      if (parts.length !== 2) {
        return;
      }

      const country = parts[0].trim().toUpperCase();
      const size = parseInt(parts[1].trim(), 10);

      // A size of 1 (or garbage) is "no rule" — never register it, so all
      // consumers can treat sizeForCountry() > 1 as "rule applies".
      if (country && !Number.isNaN(size) && size > 1) {
        this._rules[country] = size;
      }
    });

    return this._rules;
  }

  hasRules ()
  {
    return Object.keys(this.getRules()).length > 0;
  }

  // Set size for a treatment-country ISO code ('AU', 'au', …). 1 = no rule.
  sizeForCountry (isoCode)
  {
    if (!isoCode) {
      return 1;
    }

    return this.getRules()[('' + isoCode).toUpperCase()] || 1;
  }

  // Current treatment-selection country (null-safe). Race-safe: getSelection()
  // resolves from cache/metafield before app.init() has run.
  selectionCountry ()
  {
    if (!window.app || !window.app.treatmentCountry) {
      return null;
    }

    return window.app.treatmentCountry.getSelection().country;
  }

  // Set size for the customer's current treatment selection.
  sizeForSelection ()
  {
    return this.sizeForCountry(this.selectionCountry());
  }

  // Whether the rule applies to this cart line at all. Straw purchases AND
  // reservation deposits count — set-rule countries reserve in whole sets
  // too. Exempt: account-page purchases of an existing reservation
  // (quantity-1 lines of a whole-reservation-priced variant; their set
  // compliance comes from the reservation having been CREATED in whole
  // sets), portal/BE-created lines carrying __reservation_max_quantity
  // (outside theme enforcement by decision), cryo-storage lines, and
  // anything that isn't a Donor product (pregnancy quota, genetic matching,
  // storage, treatments).
  lineIsSubject (item)
  {
    if (!item || item.product_type !== 'Donor') {
      return false;
    }

    const props = item.properties || {};

    return !props.__reservation_purchase
      && !props.__reservation_max_quantity
      && !props.__cryogenic_storage_max_quantity;
  }

  // Set size for one cart line. The line's own __treatment property wins (it
  // is what the backend will see on the order); lines without one — e.g.
  // added by the customer portal — fall back to the current treatment
  // selection so they stay inside the rule. The fallback lives HERE (not in
  // callers) so no consumer can forget it.
  sizeForLine (item)
  {
    if (!this.lineIsSubject(item)) {
      return 1;
    }

    const props = item.properties || {};

    return this.sizeForCountry(props.__treatment || this.selectionCountry());
  }

  // True when the line asks for more straws than the largest whole-set
  // quantity the tracked inventory can still fulfil. Without this check a
  // stale set line (stock dropped below the cart quantity) sails through the
  // whole-set test and Shopify's checkout-time stock adjustment then reduces
  // it to a PARTIAL set — the exact thing the rule forbids. Only cart
  // payloads that carry inventory data can answer this (cart?view=custom
  // does; /cart.js does not — there this returns false and the cart-page
  // gate is the layer that catches it).
  exceedsAlignedStock (item, size)
  {
    if (item.inventory_management !== 'shopify' || item.inventory_policy !== 'deny') {
      return false;
    }

    if (typeof item.inventory_quantity !== 'number') {
      return false;
    }

    return item.quantity > item.inventory_quantity - (item.inventory_quantity % size);
  }

  // All lines of a cart (cart.js / cart?view=custom shape) that break the
  // set rule: a quantity that isn't a whole multiple of the line's set size,
  // or one that tracked stock can no longer fulfil in whole sets. Empty
  // array = cart is compliant.
  findViolations (items)
  {
    const violations = [];

    (items || []).forEach((item, index) => {
      const size = this.sizeForLine(item);

      if (size > 1 && (item.quantity % size !== 0 || this.exceedsAlignedStock(item, size))) {
        violations.push({
          item: item,
          size: size,
          line: index + 1
        });
      }
    });

    return violations;
  }

  init ()
  {
    this.initCheckoutInterception();
  }

  // Last theme-side line of defence: a native cart-form submission whose
  // submitter is the `name="checkout"` control (cart drawer button,
  // main-cart-footer, Enter-key implicit submission, requestSubmit) POSTs
  // straight to Shopify checkout with no JS involved, skipping the cart-page
  // gate entirely. Intercept at the submit event (capture phase — covers
  // every control shape, unlike a click listener), check the live cart, and
  // send violators to the cart page where the banner explains what to fix.
  // The cart page's own buttons are plain <a> elements handled by
  // custom-cart, so they never hit this. Fails open on errors and on the
  // fetch timeout — this guard must never be the thing that breaks checkout.
  initCheckoutInterception ()
  {
    if (!this.hasRules()) {
      return;
    }

    document.addEventListener('submit', e => {
      const form = e.target;
      const submitter = e.submitter;

      // Browsers without e.submitter (Safari < 15.4) skip the guard — the
      // cart-page gate still stands, and failing open beats breaking
      // checkout for a defence-in-depth layer.
      if (!form || !submitter || submitter.name !== 'checkout') {
        return;
      }

      // Fast path: subject lines carry data-straw-set-size on their rendered
      // quantity inputs (cart table + drawer). None anywhere in the DOM →
      // nothing the rule could apply to → let the submit through untouched.
      if (!document.querySelector('[data-straw-set-size]')) {
        return;
      }

      e.preventDefault();

      const passThrough = () => {
        // Re-resolve the form by id in case a section re-render replaced the
        // node while the fetch was in flight (a detached form won't submit).
        const liveForm = (form.id && document.getElementById(form.id)) || form;

        // form.submit() bypasses submit listeners (no re-entry) but drops the
        // submitter's name=checkout param, which is what makes Shopify route
        // the POST to checkout — restore it with a hidden input (the same
        // pattern as this theme's IE fallback in cart-drawer.liquid).
        if (!liveForm.querySelector('input[type="hidden"][name="checkout"]')) {
          const checkoutParam = document.createElement('input');
          checkoutParam.type = 'hidden';
          checkoutParam.name = 'checkout';
          checkoutParam.value = '';
          liveForm.appendChild(checkoutParam);
        }

        liveForm.submit();
      };

      // Hard timeout so a hung request can never leave the checkout click
      // dead — on abort we fail open like any other fetch error.
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), 6000) : null;

      fetch(window.Shopify.routes.root + 'cart.js', controller ? { signal: controller.signal } : {})
        .then(response => response.json())
        .then(cart => {
          if (this.findViolations(cart.items).length > 0) {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          } else {
            passThrough();
          }
        })
        .catch(() => passThrough())
        .finally(() => {
          if (timer) {
            clearTimeout(timer);
          }
        });
    }, true);
  }
}
